import { useCallback } from 'react';

import { useMutation } from '@apollo/client';
import type { GetContextMenuItemsParams, MenuItemDef } from 'ag-grid-community';
import { confirmPopup, ConfirmPopupProps } from 'primereact/confirmpopup';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { AuthenticationType } from '@/app/slices/authentication';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  type PastDueRestrictionResult,
  useShipmentPastDueRestriction,
} from '@/hooks/useShipmentPastDueRestriction';
import { useGenerateShippingForm } from '@/pages/shipping-details/hooks/useGenerateShippingForm';
import { useShouldPromptSemenLab } from '@/pages/shipping-details/hooks/useSemenLabPrompt';
import { setInProgress } from '@/services/shipping';
import { SHIPPING_SET_IN_PROGRESS } from '@/types.gql';
import { auditHistoryMenuItem } from '@/utils/context-menu';
import { runWorkflow, WORKFLOW } from '@/utils/workflow';

import { ShipmentStatus } from '../../utils/enums';

type Row = {
  id: string;
  hasSemen?: boolean;
  shipFromSiteId?: string | null;
  shipmentStatus?: number | null;
  shipmentNumber?: string | null;
  owner?: {
    id?: string | null;
    code?: string | null;
    name?: string | null;
  } | null;
};

type ToastRef = React.RefObject<Toast>;

export function useShippingContextMenu(
  toastRef?: ToastRef,
  onEditSelected?: (ids: string[]) => void,
) {
  const { t } = useTranslation();
  const confirmWorkflow = useConfirm();

  const generateShippingForm = useGenerateShippingForm();
  const shouldPromptSemenLab = useShouldPromptSemenLab();
  const showPedigreeNames = useFeatureFlag(
    'shipping_show_report_params_dialog',
  );
  const confirmWorkflows = useFeatureFlag('shipping_confirm_workflows');
  const {
    restrictionEnabled: pastDueRestrictionEnabled,
    checkOwners: checkPastDueOwners,
  } = useShipmentPastDueRestriction();

  const environmentId =
    useSelector(
      (s: { authentication: AuthenticationType }) =>
        s.authentication.me?.environmentId,
    ) || 0;

  const isRestForShippingEnabled = useFeatureFlag(
    '29181_shipping_graphql_to_rest',
  );
  const [setInProgressGql] = useMutation(SHIPPING_SET_IN_PROGRESS);

  const confirm = useCallback(
    (options: ConfirmPopupProps) =>
      new Promise<boolean>((resolve) => {
        confirmPopup({
          ...options,
          accept: () => resolve(true),
          reject: () => resolve(false),
        });
      }),
    [],
  );

  const withToast = useCallback(
    <F extends (...args: any[]) => Promise<any>>(label: string, fn: F) => {
      return async (...args: Parameters<F>): Promise<ReturnType<F>> => {
        toastRef?.current?.show({
          className: 'bg-white border border-gray-200 shadow-sm',
          sticky: true,
          closable: false,
          content: (
            <div className="flex w-full flex-col items-start gap-2">
              <div className="flex items-center gap-2">
                <i className="pi pi-cog pi-spin text-2xl" />
                <span className="font-medium">{label}</span>
              </div>
              <ProgressBar mode="indeterminate" className="h-1 w-full" />
            </div>
          ),
        });

        try {
          const result = (await fn(...args)) as ReturnType<F>;

          toastRef?.current?.clear();
          toastRef?.current?.show({
            severity: 'success',
            summary: t('common.complete'),
            detail: label,
            life: 2500,
          });

          return result;
        } catch (err: any) {
          const message =
            err?.message ||
            err?.networkError?.message ||
            t('common.somethingWentWrongNotifyHelpDesk');
          toastRef?.current?.clear();
          toastRef?.current?.show({
            severity: 'error',
            summary: t('common.error'),
            detail: `${label}: ${message}`,
            life: 5000,
          });
          throw err;
        }
      };
    },
    [t, toastRef],
  );

  const showPastDueRestriction = useCallback(
    (
      { accountNames, verificationFailed }: PastDueRestrictionResult,
      messageKey: string,
    ) => {
      toastRef?.current?.clear();
      toastRef?.current?.show({
        severity: 'error',
        summary: t('shipping.pastDueRestriction'),
        detail: verificationFailed
          ? t('shipping.pastDueRestrictionCheckFailed')
          : t(messageKey, { accounts: accountNames.join(', ') }),
        life: 5000,
      });
    },
    [t, toastRef],
  );

  const printForms = useCallback(
    async (rows: Row[], api: GetContextMenuItemsParams['api']) => {
      const shouldEmail = shouldPromptSemenLab(rows)
        ? await confirm({
            message: t('semen.emailSemenLab'),
            icon: 'pi pi-question-circle',
            defaultFocus: 'accept',
          })
        : false;

      await withToast(
        `Printing Shipping Form(s) (${rows.length})`,
        async () => {
          const ids = rows.map((r) => r.id);
          const shipmentNumbers = rows
            .map((r) => r.shipmentNumber)
            .filter((c): c is string => !!c);
          await generateShippingForm({
            sendEmail: shouldEmail,
            shipmentIds: ids,
            shipmentNumbers,
            showPedigree: showPedigreeNames,
          });

          api.deselectAll?.();
          api.clearRangeSelection?.();
          api.clearFocusedCell?.();
        },
      )();
    },
    [
      confirm,
      generateShippingForm,
      shouldPromptSemenLab,
      showPedigreeNames,
      t,
      withToast,
    ],
  );

  const getContextMenuItems = useCallback(
    (
      params: GetContextMenuItemsParams<any, any>,
    ): Array<string | MenuItemDef<any, any>> => {
      const api = params.api;
      const selected = (api.getSelectedRows?.() || []) as Row[];
      const focused = (params.node?.data as Row | undefined) ?? undefined;

      const rows: Row[] =
        selected.length > 0 ? selected : focused?.id ? [focused] : [];
      const ids = rows.map((r) => r.id);
      const count = ids.length;
      const has = count > 0;
      const selectedCount = selected.length;
      const hasSelected = selectedCount > 0;

      const withCount = (label: string) => `${label} (${count})`;
      const withSelectedCount = (label: string) =>
        `${label} (${selectedCount})`;

      const unselectAll = () => {
        api.deselectAll?.();
        api.clearRangeSelection?.();
        api.clearFocusedCell?.();
      };

      const doInProgressAction = withToast(
        withCount('Marking as "In Progress"'),
        async () => {
          for (const id of ids) {
            if (isRestForShippingEnabled) {
              await setInProgress(id, null);
            } else {
              await setInProgressGql({
                variables: { shipmentId: id, packedById: null },
              });
            }
          }
          api.refreshServerSide?.({ purge: false });
          unselectAll();
        },
      );

      const doInProgress = pastDueRestrictionEnabled
        ? async () => {
            const restriction = await checkPastDueOwners(
              rows.map((r) => r.owner),
            );
            if (restriction.blocked) {
              showPastDueRestriction(
                restriction,
                'shipping.pastDueRestrictionInProgress',
              );
              return;
            }
            await doInProgressAction();
          }
        : doInProgressAction;

      const executeWorkflow = (wfId: string, wfLabel: string) => {
        withToast(withCount(`Running "${wfLabel}"`), async () => {
          for (const id of ids) {
            await runWorkflow(environmentId, wfId, [{ RecordId: id }]);
          }
          unselectAll();
        })();
      };

      const runWf = async (wfId: string, wfLabel: string) => {
        if (pastDueRestrictionEnabled) {
          const restriction = await checkPastDueOwners(
            rows.map((r) => r.owner),
          );
          if (restriction.blocked) {
            showPastDueRestriction(
              restriction,
              'shipping.pastDueRestrictionWorkflow',
            );
            return;
          }
        }

        if (confirmWorkflows) {
          confirmWorkflow({
            title: 'Confirm Workflow',
            description: (
              <>
                Run <b>{wfLabel}</b> workflow for {count} shipment
                {count === 1 ? '' : 's'}?
              </>
            ),
            confirmLabel: 'OK',
            cancelLabel: 'Cancel',
            onConfirm() {
              executeWorkflow(wfId, wfLabel);
            },
          });
        } else {
          return executeWorkflow(wfId, wfLabel);
        }
      };

      const editForms = () => {
        if (!has || !ids) return;
        if (onEditSelected) {
          onEditSelected(ids);
        }
        unselectAll();
      };

      const hasShippedStatus = rows.some(
        (r) => r.shipmentStatus === ShipmentStatus.SHIPPED,
      );

      const actions: MenuItemDef = {
        name: t('common.actions'),
        disabled: !has,
        subMenu: [
          {
            name: withCount('Mark as "In Progress"'),
            disabled: !has,
            action: doInProgress,
          },
        ],
      };

      const unselectItem: MenuItemDef = {
        name: withSelectedCount('Unselect All'),
        disabled: !hasSelected,
        action: unselectAll,
      };

      const workflows: MenuItemDef = {
        name: t('common.workflows'),
        disabled: !has,
        subMenu: [
          {
            name: withCount('Ship'),
            disabled: !has,
            action: () => runWf(WORKFLOW.Ship.id, 'Ship'),
          },
          {
            name: withCount('Ship to INTRA'),
            disabled: !has,
            action: () => runWf(WORKFLOW.ShipToIntra.id, 'Ship to INTRA'),
          },
        ],
      };

      const printItem: MenuItemDef = {
        name: withCount('Print Shipping Form'),
        disabled: !has,
        action: () => {
          if (!has) return;
          return printForms(rows, api);
        },
      };

      const editItems: MenuItemDef = {
        name: withCount(t('common.editSelected')),
        disabled: !has || hasShippedStatus,
        action: editForms,
        tooltip: hasShippedStatus
          ? t('shipping.cannotEditShippedStatus')
          : undefined,
      };

      const viewHistory: MenuItemDef = {
        ...auditHistoryMenuItem({ rowId: focused?.id || '' }),
        disabled: !focused?.id,
      };

      return [
        viewHistory,
        'separator',
        actions,
        workflows,
        printItem,
        editItems,
        unselectItem,
        'separator',
        'copy',
        'copyWithHeaders',
        'paste',
      ];
    },
    [
      environmentId,
      withToast,
      printForms,
      t,
      onEditSelected,
      isRestForShippingEnabled,
      setInProgressGql,
      confirmWorkflow,
      confirmWorkflows,
      pastDueRestrictionEnabled,
      checkPastDueOwners,
      showPastDueRestriction,
    ],
  );

  return { getContextMenuItems };
}
